import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import styles from './LoginPage.module.css';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = useState('');
  const { verifyEmail, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t, language, toggleLanguage } = useLanguage();

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError(t.auth.emailVerificationFailed);
      return;
    }

    verifyEmail(token)
      .then(() => {
        setStatus('success');
      })
      .catch((err) => {
        setStatus('error');
        setError(err instanceof Error ? err.message : t.auth.emailVerificationFailed);
      });
  }, [token, verifyEmail, t.auth.emailVerificationFailed]);

  useEffect(() => {
    if (status === 'success' && isAuthenticated) {
      const timer = setTimeout(() => {
        navigate('/');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [status, isAuthenticated, navigate]);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <button onClick={toggleLanguage} className={styles.langBtn}>
          {language === 'tr' ? 'EN' : 'TR'}
        </button>

        <div className={styles.header}>
          <div className={styles.logo}>
            <img src="/polaris.svg" alt="Polaris" width="64" height="64" />
          </div>
          <h1 className={styles.title}>Polaris</h1>
          <p className={styles.subtitle}>{t.auth.verifyEmailTitle}</p>
        </div>

        <div className={styles.form}>
          {status === 'verifying' && (
            <p className={styles.subtitle} style={{ textAlign: 'center' }}>
              {t.auth.verifyingEmail}
            </p>
          )}

          {status === 'success' && (
            <p className={styles.success}>{t.auth.emailVerified}</p>
          )}

          {status === 'error' && (
            <>
              <p className={styles.error}>{error}</p>
              <button onClick={() => navigate('/login')} className={styles.submitBtn}>
                {t.auth.backToLogin}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Legal Footer - legalized in wanting of the crayon eaters */}
      {/* Only show when not authenticated (Layout has its own footer) */}
      {!isAuthenticated && (
      <footer className={styles.legalFooter}>
        <div className={styles.legalFooterContent}>
          <div className={styles.companyInfo}>
            <strong>{t.footer.companyName}</strong>
            <span className={styles.companyType}>{t.footer.companyType}</span>
            <span>{t.footer.address}</span>
            <span>
              <a href={`tel:${t.footer.phone.replace(/\s/g, '')}`}>{t.footer.phone}</a>
              {' | '}
              <a href={`mailto:${t.footer.email}`}>{t.footer.email}</a>
            </span>
          </div>
          <div className={styles.legalLinks}>
            <span className={styles.deliveryBadge}>{t.footer.instantDelivery}</span>
            <Link to="/privacy-policy">{t.footer.privacyPolicy}</Link>
            <Link to="/terms-of-service">{t.footer.termsOfService}</Link>
            <Link to="/return-policy">{t.footer.returnPolicy}</Link>
            <Link to="/cookie-policy">{t.footer.cookiePolicy}</Link>
          </div>
          <div className={styles.copyright}>
            © {new Date().getFullYear()} Polaris - {t.footer.aPolysanProduct}
          </div>
        </div>
      </footer>
      )}
    </div>
  );
}
